import React, { useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { XIcon, AlertCircleIcon } from 'lucide-react';
interface RefundModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactionAmount: number;
  transactionId: string;
  currency?: string;
  onRefund: (amount: number, reason: string, notes: string) => void;
}
export function RefundModal({
  isOpen,
  onClose,
  transactionAmount,
  transactionId,
  currency = getTenant().currency.code,
  onRefund
}: RefundModalProps) {
  const {
    t
  } = useTranslation('refunds');
  const [refundAmount, setRefundAmount] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [notes, setNotes] = useState('');
  const [showReasonError, setShowReasonError] = useState(false);
  const refundReasons = ['Requested by customer', 'Duplicated', 'Fraudulent', 'Abandoned', 'Others'];
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = 'refund-modal-title';
  const descId = 'refund-modal-desc';
  const amountId = 'refund-modal-amount';
  const amountHelpId = 'refund-modal-amount-help';
  const reasonId = 'refund-modal-reason';
  const reasonHelpId = 'refund-modal-reason-help';
  const reasonErrorId = 'refund-modal-reason-error';
  const notesId = 'refund-modal-notes';
  const notesHelpId = 'refund-modal-notes-help';
  const handleClose = () => {
    setRefundAmount('');
    setSelectedReason('');
    setNotes('');
    setShowReasonError(false);
    onClose();
  };
  // Focus management: trap focus, restore on close, Esc handler
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement;
    // Focus first focusable element in dialog
    const focusFirst = () => {
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])');
      focusable?.[0]?.focus();
    };
    const t = setTimeout(focusFirst, 0);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])')).filter(el => !el.hasAttribute('disabled'));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const handleRefundAll = () => {
    setRefundAmount(transactionAmount.toLocaleString('en-US'));
  };
  const handleRefund = () => {
    if (!selectedReason) {
      setShowReasonError(true);
      return;
    }
    const amount = parseFloat(refundAmount.replace(/,/g, '')) || 0;
    if (amount > 0 && amount <= transactionAmount) {
      onRefund(amount, selectedReason, notes);
      handleClose();
    }
  };
  const handleAmountChange = (value: string) => {
    const cleanValue = value.replace(/[^\d.]/g, '');
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formattedValue = decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
    setRefundAmount(formattedValue);
  };
  if (!isOpen) return null;
  const isRefundDisabled = !selectedReason || !refundAmount || parseFloat(refundAmount) <= 0;
  return <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={handleClose} aria-hidden="true" data-id="element-8819" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-8820">
        <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descId} className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-id="element-8821">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-8822">
            <div data-id="element-8823">
              <h2 id={titleId} className="text-xl font-semibold text-gray-900" data-id="element-8824">
                {t('components.RefundModal.refund_the_customer')}
              </h2>
              <p id={descId} className="text-sm text-gray-500 mt-1" data-id="element-8825">
                {t('components.RefundModal.refunds_take_25_days_to_appear')}
              </p>
            </div>
            <button type="button" onClick={handleClose} aria-label={t('components.RefundModal.cancel') as string} className="text-gray-400 hover:text-gray-600 transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500" data-id="element-8826">
              <XIcon size={24} aria-hidden="true" data-id="element-8827" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6" data-id="element-8828">
            {/* Refund Amount */}
            <div data-id="element-8829">
              <label htmlFor={amountId} className="block text-sm font-medium text-gray-900 mb-2" data-id="element-8830">
                {t('components.RefundModal.refund_amount')}
              </label>
              <div className="relative" data-id="element-8831">
                <input id={amountId} type="text" inputMode="decimal" value={refundAmount} onChange={e => handleAmountChange(e.target.value)} placeholder="0.00" aria-describedby={amountHelpId} aria-required="true" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg" data-id="element-8832" />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium" aria-hidden="true" data-id="element-8833">
                  {currency}
                </div>
              </div>
              <p id={amountHelpId} className="text-sm text-gray-500 mt-2" data-id="element-8834">
                {t('components.RefundModal.you_can_refund_up_to_the')}{' '}
                <span className="font-medium text-gray-900" data-id="element-8835">
                  {transactionAmount.toLocaleString('en-US')} {currency}
                </span>
                .{' '}
                <button type="button" onClick={handleRefundAll} className="text-blue-600 hover:text-blue-700 font-medium underline-offset-2 hover:underline" data-id="element-8836">
                  {t('components.RefundModal.refund_all')}
                </button>
              </p>
            </div>

            {/* Reason */}
            <div data-id="element-8837">
              <label htmlFor={reasonId} className="block text-sm font-medium text-gray-900 mb-2" data-id="element-8838">
                {t('components.RefundModal.reason')}
              </label>
              <select id={reasonId} value={selectedReason} onChange={e => {
              setSelectedReason(e.target.value);
              setShowReasonError(false);
            }} aria-required="true" aria-invalid={showReasonError || undefined} aria-describedby={`${reasonHelpId}${showReasonError ? ' ' + reasonErrorId : ''}`} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white ${showReasonError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'}`} style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%236B7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center'
            }} data-id="element-8839">
                <option value="" data-id="element-8840">
                  {t('components.RefundModal.select_a_reason')}
                </option>
                {refundReasons.map(reason => <option key={reason} value={reason} data-id="element-8841">
                    {reason}
                  </option>)}
              </select>
              <p id={reasonHelpId} className="text-sm text-gray-500 mt-2" data-id="element-8842">
                {t('components.RefundModal.this_is_only_visible_to_you')}
              </p>
              {showReasonError && <div id={reasonErrorId} role="alert" className="flex items-center gap-2 mt-2 text-sm text-red-600" data-id="element-8843">
                  <AlertCircleIcon size={16} aria-hidden="true" data-id="element-8844" />
                  <span data-id="element-8845">{t('components.RefundModal.required')}</span>
                </div>}
            </div>

            {/* Notes */}
            <div data-id="element-8846">
              <label htmlFor={notesId} className="block text-sm font-medium text-gray-900 mb-2" data-id="element-8847">
                {t('components.RefundModal.notes')}{' '}
                <span className="text-gray-400 font-normal" data-id="element-8848">
                  {t('components.RefundModal.optional')}
                </span>
              </label>
              <textarea id={notesId} value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('components.RefundModal.attrs.add_more_details_about_this_refund') as string} rows={4} aria-describedby={notesHelpId} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" data-id="element-8849" />
              <p id={notesHelpId} className="text-sm text-gray-500 mt-2" data-id="element-8850">
                {t('components.RefundModal.this_is_only_visible_to_you')}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200" data-id="element-8851">
            <button type="button" onClick={handleClose} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500" data-id="element-8852">
              {t('components.RefundModal.cancel')}
            </button>
            <button type="button" onClick={handleRefund} disabled={isRefundDisabled} className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900 ${isRefundDisabled ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-800'}`} data-id="element-8853">
              {t('components.RefundModal.refund')}
            </button>
          </div>
        </div>
      </div>
    </>;
}